import React from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Helper component for tool items
const ToolItem = ({ name, description, badge }: { name: string; description: string; badge: string }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm">{name}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
    </div>
    <Badge variant="outline" className="text-xs shrink-0">
      {badge}
    </Badge>
  </div>
);

const Mentor = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/signin");
    }
  }, [user, authLoading, navigate]);

  // Don't render if not authenticated
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight">Launch Tools</h1>
            <p className="text-muted-foreground mt-1">
              Essential tools and resources to build and launch your startup
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Building & Hosting</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Lovable" 
                  description="AI-powered app builder for Vite/React apps"
                  badge="AI Builder"
                />
                <ToolItem 
                  name="Replit" 
                  description="Cloud IDE with AI assistance"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Vercel" 
                  description="Best-in-class deployment for Next.js and React"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Netlify" 
                  description="Simple deployment with excellent DX"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Database & Auth</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Supabase" 
                  description="PostgreSQL database with authentication and real-time"
                  badge="Generous free tier"
                />
                <ToolItem 
                  name="Firebase" 
                  description="Google's backend-as-a-service platform"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Payments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Stripe" 
                  description="Industry standard payment processing"
                  badge="Pay per transaction"
                />
                <ToolItem 
                  name="Lemon Squeezy" 
                  description="Merchant of record, handles taxes for you"
                  badge="Simpler setup"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Email & Communications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Resend" 
                  description="Developer-first transactional email"
                  badge="Free tier"
                />
                <ToolItem 
                  name="MailerLite" 
                  description="Email marketing for up to 1,000 subscribers"
                  badge="Free tier"
                />
                <ToolItem 
                  name="SendGrid" 
                  description="Established email delivery platform"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Analytics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="PostHog" 
                  description="Open source product analytics"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Plausible" 
                  description="Privacy-friendly, simple analytics"
                  badge="Trial available"
                />
                <ToolItem 
                  name="Mixpanel" 
                  description="Advanced product analytics"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Monitoring & Observability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Better Stack" 
                  description="Logs and uptime monitoring"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Sentry" 
                  description="Error tracking and performance monitoring"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">AI Development Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="v0.dev" 
                  description="Vercel's AI UI component generator"
                  badge="AI powered"
                />
                <ToolItem 
                  name="Cursor" 
                  description="AI-powered code editor"
                  badge="AI coding"
                />
                <ToolItem 
                  name="Claude" 
                  description="Anthropic's AI assistant for complex tasks"
                  badge="LLM"
                />
                <ToolItem 
                  name="ChatGPT" 
                  description="OpenAI's conversational AI"
                  badge="LLM"
                />
                <ToolItem 
                  name="Gemini" 
                  description="Google's multimodal AI"
                  badge="LLM"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Design & Prototyping</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Figma" 
                  description="Collaborative design and prototyping"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Canva" 
                  description="Easy graphic design for non-designers"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Excalidraw" 
                  description="Quick sketching and diagramming"
                  badge="Free"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Customer Support</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Intercom" 
                  description="Complete customer messaging platform"
                  badge="Trial available"
                />
                <ToolItem 
                  name="Plain" 
                  description="Modern, lightweight customer support"
                  badge="Developer-friendly"
                />
                <ToolItem 
                  name="Crisp" 
                  description="Multi-channel support (chat, email, social)"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Project Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Linear" 
                  description="Issue tracking built for modern product teams"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Notion" 
                  description="All-in-one workspace for docs, tasks, and wikis"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Trello" 
                  description="Simple kanban boards for task management"
                  badge="Free tier"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Marketing & SEO</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Mailchimp" 
                  description="Email marketing and automation"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Buffer" 
                  description="Social media scheduling and analytics"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Ahrefs" 
                  description="SEO tools and competitor analysis"
                  badge="Trial available"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Legal & Compliance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToolItem 
                  name="Termly" 
                  description="Generate privacy policies and terms of service"
                  badge="Free tier"
                />
                <ToolItem 
                  name="Stripe Atlas" 
                  description="Incorporate a Delaware C-Corp with banking"
                  badge="$500 one-time"
                />
                <ToolItem 
                  name="Clerky" 
                  description="Legal paperwork for startups"
                  badge="Affordable"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mentor;
